import React, { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { FaEdit, FaPlus, FaTimes, FaTrash } from "react-icons/fa";
import { Link } from "react-router-dom";
import { useAuth } from "../Login/AuthContext";

const ALERT_COLOR = "#92400e";

const ListaProductos = () => {
  const [productos, setProductos] = useState([]);
  const [filteredProductos, setFilteredProductos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categorias, setCategorias] = useState([]);
  const [productoEditando, setProductoEditando] = useState(null);
  const [filtros, setFiltros] = useState({
    nombre: "",
    descripcion: "",
    precio: "",
    stock: "",
    categoria: "",
  });

  const API_URL = process.env.REACT_APP_API_URL;
  const BASE_URL = process.env.REACT_APP_BACKEND_URL;
  const { token } = useAuth();

  useEffect(() => {
    const controller = new AbortController();
    let activo = true;

    const fetchProductos = async () => {
      try {
        const response = await axios.get(`${API_URL}/productos`, {
          signal: controller.signal,
        });
        if (!activo) return;
        if (Array.isArray(response.data)) {
          setProductos(response.data);
          setFilteredProductos(response.data);
        } else {
          setError("Error: Los datos no son un arreglo.");
        }
        setLoading(false);
      } catch (err) {
        if (!axios.isCancel(err)) {
          if (!activo) return;
          setError("Error al cargar los productos. Por favor, intente nuevamente.");
          setLoading(false);
        }
      }
    };

    fetchProductos();

    return () => {
      activo = false;
      controller.abort();
    };
  }, [API_URL]);

  useEffect(() => {
    const controller = new AbortController();

    const fetchCategorias = async () => {
      try {
        const response = await axios.get(`${API_URL}/categorias`, {
          signal: controller.signal,
        });
        setCategorias(response.data);
      } catch (err) {
        if (!axios.isCancel(err)) {
          console.error("Error al cargar categorias:", err);
        }
      }
    };

    fetchCategorias();

    return () => controller.abort();
  }, [API_URL]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    const nuevosFiltros = { ...filtros, [name]: value };
    setFiltros(nuevosFiltros);
    filtrarProductos(nuevosFiltros);
  };

  const filtrarProductos = (filtrosActuales) => {
    const productosFiltrados = productos.filter((producto) => {
      const categoriaNombre = producto.categoria?.nombre || "";
      return (
        producto.nombre?.toLowerCase().includes(filtrosActuales.nombre.toLowerCase()) &&
        (producto.descripcion || "").toLowerCase().includes(filtrosActuales.descripcion.toLowerCase()) &&
        producto.precio?.toString().includes(filtrosActuales.precio) &&
        producto.stock?.toString().includes(filtrosActuales.stock) &&
        categoriaNombre.toLowerCase().includes(filtrosActuales.categoria.toLowerCase())
      );
    });
    setFilteredProductos(productosFiltrados);
  };

  const eliminarProducto = async (id) => {
    const result = await Swal.fire({
      title: "Estas seguro?",
      text: "No podras revertir esta accion.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: ALERT_COLOR,
      cancelButtonColor: "#64748b",
      confirmButtonText: "Si, eliminar",
      cancelButtonText: "Cancelar",
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(`${API_URL}/productos/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        setProductos(productos.filter((p) => p.id !== id));
        setFilteredProductos(filteredProductos.filter((p) => p.id !== id));

        Swal.fire({
          title: "Eliminado",
          text: "El producto ha sido eliminado con exito.",
          icon: "success",
          confirmButtonColor: ALERT_COLOR,
        });
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "No se pudo eliminar el producto.",
          confirmButtonColor: ALERT_COLOR,
        });
      }
    }
  };

  const actualizarProductoEnLista = (productoActualizado) => {
    const actualizar = (lista) =>
      lista.map((producto) =>
        producto.id === productoActualizado.id ? productoActualizado : producto
      );

    setProductos((prev) => actualizar(prev));
    setFilteredProductos((prev) => actualizar(prev));
  };

  if (loading) {
    return (
      <EstadoListado texto="Cargando productos..." animado />
    );
  }

  if (error) {
    return (
      <EstadoListado texto={error} />
    );
  }

  return (
    <section className="min-h-screen flex flex-col items-center p-4 md:p-8 bg-stone-50">
      <div className="w-full max-w-7xl p-4 md:p-6 border border-stone-200 rounded-lg shadow-lg bg-white">
        <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <span className="text-sm font-semibold uppercase tracking-wide text-amber-700">
              Administracion de catalogo
            </span>
            <h2 className="mt-2 text-2xl md:text-3xl font-extrabold text-stone-900">
              Listado de Productos
            </h2>
          </div>

          <Link
            to="/admin/productos/registro"
            className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-stone-900 px-5 py-3 text-sm font-semibold text-white shadow-md transition hover:bg-amber-800 md:w-auto"
          >
            <FaPlus />
            Agregar producto
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
          {["nombre", "descripcion", "precio", "stock", "categoria"].map((campo) => (
            <input
              key={campo}
              type="text"
              name={campo}
              placeholder={`Filtrar por ${campo}`}
              value={filtros[campo]}
              onChange={handleFilterChange}
              className="border border-stone-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-600"
            />
          ))}
        </div>

        <div className="overflow-x-auto rounded-lg border border-stone-200">
          <table className="table-auto w-full min-w-[900px] border-collapse">
            <thead>
              <tr className="bg-stone-100 text-stone-900">
                <th className="border border-stone-200 px-4 py-2">Imagen</th>
                <th className="border border-stone-200 px-4 py-2">Nombre</th>
                <th className="border border-stone-200 px-4 py-2">Descripcion</th>
                <th className="border border-stone-200 px-4 py-2">Precio</th>
                <th className="border border-stone-200 px-4 py-2">Stock</th>
                <th className="border border-stone-200 px-4 py-2">Categoria</th>
                <th className="border border-stone-200 px-4 py-2">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredProductos.length === 0 ? (
                <tr>
                  <td className="px-4 py-8 text-center text-stone-500" colSpan="7">
                    No hay productos para mostrar.
                  </td>
                </tr>
              ) : (
                filteredProductos.map((producto, index) => (
                  <tr
                    key={producto.id}
                    className={`${index % 2 === 0 ? "bg-white" : "bg-stone-50"} hover:bg-amber-50`}
                  >
                    <td className="border border-stone-200 px-4 py-2 text-center">
                      {producto.imagen_url ? (
                        <img
                          src={`${BASE_URL}${producto.imagen_url}`}
                          alt={producto.nombre}
                          className="w-16 h-16 object-cover rounded mx-auto"
                        />
                      ) : (
                        <span className="text-stone-400">Sin imagen</span>
                      )}
                    </td>
                    <td className="border border-stone-200 px-4 py-2 text-stone-800">
                      {producto.nombre}
                    </td>
                    <td className="border border-stone-200 px-4 py-2">
                      {producto.descripcion ? (
                        <DescripcionExpandable descripcion={producto.descripcion} />
                      ) : ""}
                    </td>
                    <td className="border border-stone-200 px-4 py-2 font-semibold text-stone-900">
                      $ {producto.precio}
                    </td>
                    <td className="border border-stone-200 px-4 py-2 text-stone-800">
                      {producto.stock}
                    </td>
                    <td className="border border-stone-200 px-4 py-2 text-stone-800">
                      {producto.categoria?.nombre || ""}
                    </td>
                    <td className="border border-stone-200 px-4 py-2 text-center">
                      <button
                        onClick={() => setProductoEditando(producto)}
                        className="mr-3 text-stone-900 hover:text-amber-800"
                        title="Editar"
                        aria-label={`Editar ${producto.nombre}`}
                      >
                        <FaEdit />
                      </button>
                      <button
                        onClick={() => eliminarProducto(producto.id)}
                        className="text-stone-900 hover:text-amber-800"
                        title="Eliminar"
                      >
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      {productoEditando && (
        <ModalEditarProducto
          producto={productoEditando}
          categorias={categorias}
          apiUrl={API_URL}
          baseUrl={BASE_URL}
          token={token}
          onClose={() => setProductoEditando(null)}
          onUpdated={actualizarProductoEnLista}
        />
      )}
    </section>
  );
};

const EstadoListado = ({ texto, animado = false }) => (
  <div className="flex justify-center items-center min-h-screen bg-stone-50">
    <p className={`text-stone-700 text-lg ${animado ? "animate-pulse" : ""}`}>
      {texto}
    </p>
  </div>
);

const DescripcionExpandable = ({ descripcion }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  return (
    <div>
      <p className="text-stone-700">
        {isExpanded ? descripcion : descripcion?.slice(0, 50) + (descripcion?.length > 50 ? "..." : "")}
      </p>
      {descripcion && descripcion.length > 50 && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-amber-800 hover:underline text-sm"
        >
          {isExpanded ? "Ver menos" : "Ver mas"}
        </button>
      )}
    </div>
  );
};

const ModalEditarProducto = ({
  producto,
  categorias,
  apiUrl,
  baseUrl,
  token,
  onClose,
  onUpdated,
}) => {
  const [form, setForm] = useState({
    nombre: producto.nombre || "",
    descripcion: producto.descripcion || "",
    precio: producto.precio?.toString() || "",
    stock: producto.stock?.toString() || "",
    categoriaId: producto.categoria?.id?.toString() || "",
  });
  const [imagen, setImagen] = useState(null);
  const [preview, setPreview] = useState(
    producto.imagen_url ? `${baseUrl}${producto.imagen_url}` : ""
  );
  const [guardando, setGuardando] = useState(false);

  const handleChange = (campo, valor) => {
    setForm((prev) => ({ ...prev, [campo]: valor }));
  };

  const mostrarError = (text) => {
    Swal.fire({
      icon: "error",
      title: "Error",
      text,
      confirmButtonColor: ALERT_COLOR,
    });
  };

  const validar = () => {
    if (!form.nombre.trim()) {
      mostrarError("El nombre del producto es obligatorio.");
      return false;
    }
    if (!form.precio || isNaN(form.precio) || Number(form.precio) < 0) {
      mostrarError("El precio debe ser un numero valido mayor o igual a 0.");
      return false;
    }
    if (
      !form.stock ||
      isNaN(form.stock) ||
      !Number.isInteger(Number(form.stock)) ||
      Number(form.stock) < 0
    ) {
      mostrarError("El stock debe ser un numero entero valido mayor o igual a 0.");
      return false;
    }
    if (!form.categoriaId) {
      mostrarError("Debe seleccionar una categoria.");
      return false;
    }
    if (!preview) {
      mostrarError("El producto debe tener una imagen.");
      return false;
    }
    return true;
  };

  const subirImagen = async () => {
    if (!imagen) return producto.imagen_url;

    const formData = new FormData();
    formData.append("imagen", imagen);

    const response = await axios.post(`${apiUrl}/upload/producto-imagen`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data.imagen_url;
  };

  const handleImagenChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImagen(file);
    setPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validar()) return;

    try {
      setGuardando(true);
      const imagenUrl = await subirImagen();

      const payload = {
        nombre: form.nombre.trim(),
        descripcion: form.descripcion.trim(),
        precio: Number(form.precio),
        stock: Number(form.stock),
        categoria_id: Number(form.categoriaId),
        imagen_url: imagenUrl,
      };

      const response = await axios.put(`${apiUrl}/productos/${producto.id}`, payload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      onUpdated(response.data);
      Swal.fire({
        icon: "success",
        title: "Producto actualizado",
        text: "Los cambios se guardaron correctamente.",
        confirmButtonColor: ALERT_COLOR,
      });
      onClose();
    } catch (error) {
      const msg = error.response?.data?.message || error.response?.data?.msg || "";
      Swal.fire({
        icon: "error",
        title: "Error",
        text: msg || "No se pudo actualizar el producto.",
        confirmButtonColor: ALERT_COLOR,
      });
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4 py-6">
      <div className="relative w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-lg bg-white p-5 shadow-2xl md:p-8">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 text-stone-600 hover:text-stone-900"
          aria-label="Cerrar"
        >
          <FaTimes />
        </button>

        <div className="mb-6 text-center">
          <span className="text-sm font-semibold uppercase tracking-wide text-amber-700">
            Catalogo de arte
          </span>
          <h3 className="mt-2 text-2xl font-extrabold text-stone-900">
            Editar producto
          </h3>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col space-y-4">
          <CampoTexto
            label="Nombre del producto"
            value={form.nombre}
            onChange={(valor) => handleChange("nombre", valor)}
            required
          />
          <CampoArea
            label="Descripcion"
            value={form.descripcion}
            onChange={(valor) => handleChange("descripcion", valor)}
          />
          <CampoTexto
            label="Precio"
            value={form.precio}
            onChange={(valor) => handleChange("precio", valor)}
            type="number"
            required
          />
          <CampoTexto
            label="Stock"
            value={form.stock}
            onChange={(valor) => handleChange("stock", valor)}
            type="number"
            required
          />
          <div className="flex flex-col">
            <label className="mb-2 text-stone-800 font-medium">Categoria</label>
            <select
              value={form.categoriaId}
              onChange={(e) => handleChange("categoriaId", e.target.value)}
              required
              className={fieldClassName}
            >
              <option value="">Seleccione una categoria</option>
              {categorias.map((categoria) => (
                <option key={categoria.id} value={categoria.id}>
                  {categoria.nombre}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col">
            <label className="mb-2 text-stone-800 font-medium">
              Imagen del producto
            </label>
            <input
              type="file"
              accept="image/png, image/jpeg, image/jpg, image/webp"
              onChange={handleImagenChange}
              className="border border-stone-300 bg-white p-2 rounded-md w-full focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-600"
            />
          </div>

          {preview && (
            <div className="flex justify-center">
              <img
                src={preview}
                alt="Vista previa"
                className="h-40 w-40 rounded-lg border border-stone-200 object-cover shadow"
              />
            </div>
          )}

          <div className="flex flex-col gap-3 pt-4 sm:flex-row">
            <button
              type="button"
              onClick={onClose}
              className="w-full rounded-lg bg-stone-100 p-3 font-semibold text-stone-800 transition hover:bg-stone-200 sm:w-1/2"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={guardando}
              className="w-full rounded-lg bg-stone-900 p-3 font-semibold text-white shadow-md transition hover:bg-amber-800 disabled:opacity-60 sm:w-1/2"
            >
              {guardando ? "Guardando..." : "Guardar cambios"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const fieldClassName =
  "border border-stone-300 bg-white p-3 rounded-md w-full focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-600";

const CampoTexto = ({ label, value, onChange, type = "text", required = false }) => (
  <div className="flex flex-col">
    <label className="mb-2 text-stone-800 font-medium">{label}</label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      className={fieldClassName}
    />
  </div>
);

const CampoArea = ({ label, value, onChange }) => (
  <div className="flex flex-col">
    <label className="mb-2 text-stone-800 font-medium">{label}</label>
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      rows="4"
      className={fieldClassName}
    />
  </div>
);

export default ListaProductos;
